import _ from 'lodash';
import {connect} from 'react-redux';

import {
  MessageStatusesEnum,
  MessageVisualStatusesEnum,
} from '../../../../../../libs/shared/core/src/models/api/messageModel';
import {
  ComposerPanelId,
  RViewerPanelId,
  ViewerPanelId,
} from '../../../../../../libs/shared/core/src/store/panels/panelStateBase';
import {createSelector, makeMapStateToProps} from '../../../../../../srcCommon/helpers/reselectHelpers';
import {ApplicationComposerToolbarEntryPointModel} from '../../../../../models/api/applicationEntryPointModel';
import {AppState} from '../../../../../store/appState';
import {getApplications} from '../../../../../store/data/applications/applicationsSelectors';
import {getMessages} from '../../../../../store/data/dataSelectors';
import {makeGetIsPanelDraftOwner} from '../../../../../store/data/messages/messagesSelectors';
import {ApplicationPanelLocationsEnum} from '../../../../../store/panels/applications/applicationPanelState';
import {getFlatPanels} from '../../../../../store/panels/panelsSelectors';
import {panelIsApplicationsPanel, panelIsViewerPanel} from '../../../../../store/panels/panelsState';
import {
  RViewerPanelState,
  ViewerPanelPageTypesEnum,
} from '../../../../../store/panels/viewer/viewerPanelState';
import {ApplicationComposerEntryPointButton} from './applicationComposerEntryPointButton';

/*
 * Props.
 */

interface ApplicationComposerEntryPointButtonContainerProps {
  panelId: ComposerPanelId | ViewerPanelId;
  entryPoint: ApplicationComposerToolbarEntryPointModel;
  draftMessageUid: string;
}

/*
 * Selectors.
 */

function getEntryPoint(_state: AppState, props: ApplicationComposerEntryPointButtonContainerProps) {
  return props.entryPoint;
}

function getPanelId(_state: AppState, props: ApplicationComposerEntryPointButtonContainerProps) {
  return props.panelId;
}

function getDraftMessageUid(_state: AppState, props: ApplicationComposerEntryPointButtonContainerProps) {
  return props.draftMessageUid;
}

const getEntryPointPanel = createSelector(
  [getEntryPoint, getFlatPanels, getPanelId],
  (entryPoint, panels, panelId) =>
    _(panels)
      .filter(panelIsApplicationsPanel)
      .filter(
        (p) =>
          p.location === ApplicationPanelLocationsEnum.POPOVER &&
          p.frame.frameId === entryPoint.frameId &&
          p.parentPanelId === panelId,
      )
      .first(),
);

const getApplication = createSelector([getApplications, getEntryPoint], (applications, entryPoint) => {
  const application = applications[entryPoint.applicationId];
  if (!application) {
    throw new Error('Could not find application for entry point');
  }
  return application;
});

function makeGetCellsContext(getViewerPanelId: any) {
  return createSelector([getViewerPanelId, getFlatPanels], (viewerPanelId, panels) => {
    if (!viewerPanelId) {
      return undefined;
    }
    const panel = panels.find((p) => p.id === viewerPanelId);
    if (!panel || !RViewerPanelState.is(panel)) {
      return undefined;
    }
    return panel.pages.current.cellsContext;
  });
}

function makeGetMessage() {
  return createSelector(
    [getDraftMessageUid, getMessages, makeGetIsPanelDraftOwner(getPanelId)],
    (messageUid, messages, isPanelDraftOwner) => {
      if (!isPanelDraftOwner(messageUid)) {
        return undefined;
      }

      const message = messages[messageUid];
      if (
        !message ||
        message.visualStatus !== MessageVisualStatusesEnum.DRAFT ||
        message.status !== MessageStatusesEnum.DRAFT_COMPOSE
      ) {
        return undefined;
      }

      return message;
    },
  );
}

function makeGetViewerPanelId() {
  return createSelector([getPanelId, getFlatPanels, makeGetMessage()], (panelId, panels, message) => {
    if (RViewerPanelId.is(panelId)) {
      return panelId;
    }
    const panel = panels.find(
      (p) =>
        panelIsViewerPanel(p) &&
        p.pages.current.type === ViewerPanelPageTypesEnum.SINGLE_CONVERSATION &&
        p.pages.current.conversationId === message?.conversationId,
    );
    if (panel && RViewerPanelId.is(panel.id)) {
      return panel.id;
    }
    return undefined;
  });
}

/*
 * Redux.
 */

const mapStateToProps = makeMapStateToProps<ApplicationComposerEntryPointButtonContainerProps>()(() => {
  const getViewerPanelId = makeGetViewerPanelId();
  const getMessage = makeGetMessage();
  return {
    panel: getEntryPointPanel,
    application: getApplication,
    cellsContext: makeGetCellsContext(getViewerPanelId),
    draftMessage: getMessage,
    viewerPanelId: makeGetViewerPanelId(),
  };
});

export const ApplicationComposerEntryPointButtonContainer = connect(mapStateToProps)(
  ApplicationComposerEntryPointButton,
);
